<template>
    <div class="start-chat-overlay">
        <div v-if="assistent?.features.requestContactDataEnabled" class="start-chat-overlay__user-contact">
            <user-contact
                ref="userContactForm"
                :required-contact-data-fields="requiredContactDataFields"
            />
        </div>

        <i18n-t keypath="privacyStatement" tag="div">
            <template #link>
                <span
                    class="privacy-statement"
                    @click="showPrivacyStatement"
                    @keydown.enter="showPrivacyStatement"
                >
                    {{ t('privacyStatementLink') }}
                </span>
            </template>
        </i18n-t>

        <button
            :class="buttonClasses"
            :disabled="!canStartChat"
            type="button"
            @click="onStartChatClicked"
        >
            Ga verder
            <icon-chevron-right style="height: 2rem" />
        </button>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import useConfiguration from '@/composables/useConfiguration';
    import useWidgetState, { State } from '@/composables/useWidgetState';
    import IconChevronRight from '@/assets/icons/chevron_right.svg?component';
    import { useI18n } from 'vue-i18n';
    import messages from './StartChatOverlay.messages.json';
    import Chat from '@/chat/Chat';
    import { VisitorInteractionType } from '@keyvue/one-api-client';
    import useVisitorData from '@/composables/useVisitorData';
    import UserContact from '@/components/UserContact.vue';

    const chat = Chat.getInstance();
    const { t } = useI18n({ messages });
    const { registerInteraction } = useVisitorData();
    const { isLoaded, assistent } = useConfiguration();
    const { state, showStartChatOverlay, showPrivacyStatementOverlay, agreedToPrivacyStatement } = useWidgetState();

    const userContactForm = ref();

    const canStartChat = computed(() => isLoaded && (!requiresContactData.value || (userContactForm.value && userContactForm.value.isFormValid)));
    const buttonClasses = computed(() => {
        return {
            'start-chat-overlay__button': true,
            'start-chat-overlay__button--disabled': !canStartChat.value,
        };
    });

    const requiredContactDataFields = assistent.value?.features.requiredContactDataFields ?? [];

    function showPrivacyStatement() {
        showPrivacyStatementOverlay.value = true;
    }

    async function onStartChatClicked() {
        if (!assistent.value?.id.value) {
            return;
        }

        showStartChatOverlay.value = false;
        agreedToPrivacyStatement.value = true;
        state.value = State.Opened;
        await chat.agreeToPrivacyTerms();
        await registerInteraction(VisitorInteractionType.TERMS_ACCEPTED);
    }

    const requiresContactData = computed(() => (
        assistent && assistent.value?.features.requestContactDataEnabled
    ));
</script>

<style scoped lang="scss">
    .start-chat-overlay {
        background-color: white;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 1.5rem 1.5rem;
        gap: 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        border-top: solid 1px lightgray;
        transition: transform 200ms ease-in-out;
        transform: translateY(0);
        overflow-y: scroll;
        max-height: 100%;

        &__user-contact {
            width: 100%;
        }

        &__button {
            background-color: var(--color-widget);
            color: var(--color-widget-text);
            border: none;
            font-weight: bold;
            border-radius: 10px;
            cursor: pointer;
            display: flex;
            align-items: center;
            padding: .5rem 3rem .5rem 3.5rem;

            &:hover {
                filter: brightness(1.1);
            }

            &--disabled {
                background-color: #c3c3c3;
                cursor: unset;

                &:hover {
                    filter: none;
                }
            }
        }
    }

    .privacy-statement {
        cursor: pointer;
        color: var(--color-widget);
        font-weight: bold;
    }

</style>
